import React, { useCallback, useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  FileText, Upload, Trash2, Download, Plus, Bell, ExternalLink, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { ZuordnungPicker, ZuordnungLabel, istNeu } from "@/components/portal/ZuordnungPicker";

const fmtBytes = (n) => {
  if (!n) return "–";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export const AdminDokumenteTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOffen, setDialogOffen] = useState(false);

  const laden = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/documents?include_inaktiv=true");
      setItems(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const loeschen = async (id) => {
    if (!window.confirm("Dokument wirklich löschen?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Dokument gelöscht.");
      laden();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.dateiname || "dokument";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setDialogOffen(true)} data-testid="dokument-neu">
          <Plus className="h-4 w-4 mr-2" /> Neues Dokument
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5 text-primary" />
            Dokumente ({items.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Noch keine Dokumente. Klicken Sie auf „Neues Dokument“, um zu starten.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titel</TableHead>
                    <TableHead>Zielgruppe</TableHead>
                    <TableHead>Gültig</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Größe</TableHead>
                    <TableHead className="text-right">Downloads</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((d) => (
                    <TableRow key={d.id} data-testid={`dok-row-${d.id}`}>
                      <TableCell>
                        <div className="font-medium flex items-center gap-2">
                          {d.titel}
                          {istNeu(d.erstellt_am, d.neu_badge_tage) && (
                            <Badge className="text-[10px] bg-primary/10 text-primary border-primary/30" variant="outline">Neu</Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">{d.dateiname}</div>
                      </TableCell>
                      <TableCell><ZuordnungLabel typ={d.zuordnung_typ} ids={d.zuordnung_ids} /></TableCell>
                      <TableCell className="text-xs">
                        {d.gueltig_ab ? fmtDate(d.gueltig_ab) : "sofort"} –{" "}
                        {d.gueltig_bis ? fmtDate(d.gueltig_bis) : "unbegrenzt"}
                      </TableCell>
                      <TableCell>
                        {d.aktiv ? <Badge variant="default">aktiv</Badge>
                                : <Badge variant="outline">inaktiv</Badge>}
                      </TableCell>
                      <TableCell className="text-right text-xs">{fmtBytes(d.groesse_bytes)}</TableCell>
                      <TableCell className="text-right">{d.downloads || 0}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => download(d)}
                            data-testid={`dok-download-${d.id}`} title="Herunterladen">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => loeschen(d.id)}
                            data-testid={`dok-delete-${d.id}`} title="Löschen">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {dialogOffen && (
        <UploadDialog
          onClose={() => setDialogOffen(false)}
          onDone={() => { setDialogOffen(false); laden(); }} />
      )}
    </div>
  );
};

const UploadDialog = ({ onClose, onDone }) => {
  const [datei, setDatei] = useState(null);
  const [titel, setTitel] = useState("");
  const [beschreibung, setBeschreibung] = useState("");
  const [zuord, setZuord] = useState({ zuordnung_typ: "alle", zuordnung_ids: [] });
  const [gueltigAb, setGueltigAb] = useState("");
  const [gueltigBis, setGueltigBis] = useState("");
  const [neuBadgeTage, setNeuBadgeTage] = useState(7);
  const [benachrichtigen, setBenachrichtigen] = useState(false);
  const [running, setRunning] = useState(false);

  const speichern = async () => {
    if (!datei) { toast.error("Bitte eine Datei wählen."); return; }
    if (!titel.trim()) { toast.error("Bitte einen Titel eingeben."); return; }
    if (zuord.zuordnung_typ !== "alle" && zuord.zuordnung_ids.length === 0) {
      toast.error("Bitte mindestens einen Eintrag in der Zielgruppe wählen.");
      return;
    }
    setRunning(true);
    try {
      const fd = new FormData();
      fd.append("file", datei);
      fd.append("titel", titel);
      fd.append("beschreibung", beschreibung);
      fd.append("zuordnung_typ", zuord.zuordnung_typ);
      fd.append("zuordnung_ids", zuord.zuordnung_ids.join(","));
      if (gueltigAb) fd.append("gueltig_ab", gueltigAb);
      if (gueltigBis) fd.append("gueltig_bis", gueltigBis);
      fd.append("neu_badge_tage", String(neuBadgeTage));
      fd.append("benachrichtigen", String(benachrichtigen));
      await api.post("/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Dokument hochgeladen.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRunning(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" /> Dokument hochladen
          </DialogTitle>
          <DialogDescription>
            Maximal 25 MB. Zielgruppe steuert, welche Lieferanten das Dokument sehen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-sm">Datei</Label>
            <Input type="file" onChange={(e) => setDatei(e.target.files?.[0] || null)}
              accept=".pdf,image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
              data-testid="dok-file" />
            {datei && <p className="mt-1 text-xs text-muted-foreground">{datei.name} · {fmtBytes(datei.size)}</p>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Titel</Label>
              <Input value={titel} onChange={(e) => setTitel(e.target.value)}
                placeholder="z. B. Milchpreisrundschreiben" data-testid="dok-titel" />
            </div>
            <div>
              <Label className="text-sm">Beschreibung (optional)</Label>
              <Input value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} />
            </div>
          </div>
          <ZuordnungPicker value={zuord} onChange={setZuord} testIdPrefix="dok-zuord" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-sm">Gültig ab</Label>
              <Input type="date" value={gueltigAb} onChange={(e) => setGueltigAb(e.target.value)} />
            </div>
            <div>
              <Label className="text-sm">Gültig bis</Label>
              <Input type="date" value={gueltigBis} onChange={(e) => setGueltigBis(e.target.value)} />
            </div>
            <div>
              <Label className="text-sm">Neu-Badge (Tage)</Label>
              <Input type="number" min={0} max={30} value={neuBadgeTage}
                onChange={(e) => setNeuBadgeTage(Number(e.target.value))} />
            </div>
          </div>
          <label className="flex items-center gap-2 rounded-md border p-2 bg-secondary/40 cursor-pointer">
            <Switch checked={benachrichtigen} onCheckedChange={setBenachrichtigen}
              data-testid="dok-benachrichtigen" />
            <div className="flex-1">
              <p className="text-sm flex items-center gap-1"><Bell className="h-3.5 w-3.5" /> Betroffene Lieferanten benachrichtigen</p>
              <p className="text-xs text-muted-foreground">In-App + E-Mail (wenn SMTP aktiv)</p>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={speichern} disabled={running} data-testid="dok-speichern">
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
            Veröffentlichen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
